import { useFormik, FormikHelpers, FormikProps } from "formik";
import * as Yup from "yup";
import { useAxios } from "../../../hooks";
import { CreateAccountValidationSchema } from "@next-pontos/validations";
import { Program } from "../new-bid-form-step-one";
import { RefetchFunction } from "axios-hooks";

export interface CreateAccountFormValues {
  company: string;
  holder_name: string;
  holder_email: string;
  holder_cpf: string;
  holder_phone_number: string;
  login: string;
  password: string;
  is_membership_active: boolean;
  holder_birthday: string;
  membership_number: string;
}

interface UseCreateAccountDataProps<T> {
  formik: FormikProps<T>;
  refetchAccounts: RefetchFunction<any, any>;
  programs: { data: Program[] };
  createAccountModalVisibilityControls: { setFalse: () => void };
}

export const useCreateAccountData = <T,>({
  formik,
  programs,
  createAccountModalVisibilityControls,
  refetchAccounts,
}: UseCreateAccountDataProps<T>) => {
  const [account, createAccount] = useAxios(
    { url: "/account", method: "POST" },
    { manual: true }
  );

  const createAccountFormik = useFormik<CreateAccountFormValues>({
    initialValues: {
      company: "",
      holder_name: "",
      holder_email: "",
      holder_cpf: "",
      holder_phone_number: "",
      login: "",
      password: "",
      is_membership_active: false,
      holder_birthday: "",
      membership_number: "",
    },
    onSubmit: async (values, formikHelpers: FormikHelpers<CreateAccountFormValues>) => {
      const createdAccount = await createAccount({ data: values });
      formik.setFieldValue("account_id", createdAccount.data.id);
      await refetchAccounts();
      createAccountModalVisibilityControls.setFalse();
      formikHelpers.resetForm();
    },
    validationSchema: CreateAccountValidationSchema.concat(
      Yup.object({
        holder_email: Yup.string()
          .email("Deve ser um e-mail válido")
          .required("Este campo é obrigatório")
          .test(
            "no-outlook-email",
            "E-mails do Outlook não são permitidos para esta empresa",
            function (value) {
              const { company } = this.parent;
              const program = programs.data?.find((p: Program) => p.company === company);
              return program?.needs_email_redirect && value?.endsWith("@outlook.com") ? false : true;
            }
          ),
      })
    ),
    validateOnMount: true,
  });

  return {
    account,
    createAccountFormik,
    createAccountModalVisibility: createAccountModalVisibilityControls.setFalse,
  };
};
